// 📎 파일명·확장자 유틸 (2026-08-13 사용자: "다운로드 받을 때 확장자가 안 붙는 경우가 생긴다")
//   근본 원인: 전달된 파일의 첨부 URL 은 `/api/tpt/file?share=<토큰>` 이라 **경로에 파일명이 없다**.
//   URL 에서 파일명을 유도하는 경로(<a download> 안전망 등)가 "file" 을 뽑아 확장자 없이 저장됐다.
//   → 이름 · URL · MIME 세 출처를 순서대로 훑어 확장자를 반드시 복원하고, 저장 다이얼로그에도
//     그 확장자를 필터로 넘겨(OS 가 자동 부착) 확장자 유실을 원천 차단한다.

/** 확장자로 인정할 후보 — 순수 숫자는 제외(`report_2026.08.13` 의 `13` 을 확장자로 오인 방지). */
const EXT_RE = /\.([A-Za-z0-9]{1,8})$/;
/** 파일명 구실을 못 하는 일반 폴백 — 이 이름이면 URL·MIME 쪽 이름을 우선한다. */
const GENERIC_NAMES = new Set(['file', 'download', 'attachment', 'blob', '파일', 'unnamed']);
/** 사이드카/RC 라우트 경로 조각 — basename 이 이것들이면 파일명이 아니다. */
const ROUTE_BASENAMES = new Set(['file', 'download', 'files', 'api', 'tpt', 'zip', 'preview']);

const MIME_EXT: Record<string, string> = {
	'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/gif': 'gif',
	'image/webp': 'webp', 'image/svg+xml': 'svg', 'image/bmp': 'bmp', 'image/heic': 'heic',
	'application/pdf': 'pdf',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
	'application/vnd.ms-excel': 'xls',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
	'application/msword': 'doc',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
	'application/vnd.ms-powerpoint': 'ppt',
	'application/haansofthwp': 'hwp', 'application/x-hwp': 'hwp',
	'application/zip': 'zip', 'application/x-zip-compressed': 'zip',
	'application/x-rar-compressed': 'rar', 'application/x-7z-compressed': '7z',
	'application/gzip': 'gz', 'application/x-tar': 'tar',
	'text/plain': 'txt', 'text/csv': 'csv', 'text/html': 'html', 'text/markdown': 'md',
	'application/json': 'json', 'application/xml': 'xml', 'text/xml': 'xml',
	'video/mp4': 'mp4', 'video/webm': 'webm', 'video/quicktime': 'mov', 'video/x-msvideo': 'avi',
	'audio/mpeg': 'mp3', 'audio/wav': 'wav', 'audio/x-wav': 'wav', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg',
	'application/vnd.android.package-archive': 'apk',
	'application/octet-stream': '',   // 의미 없음 — 확장자 추론 불가
};

/** 파일명의 확장자(소문자, 점 제외). 없으면 ''. 순수 숫자 꼬리는 확장자로 보지 않는다. */
export function extOf(name: string | null | undefined): string {
	const m = EXT_RE.exec(String(name || '').trim());
	if (!m) return '';
	const ext = m[1].toLowerCase();
	if (/^[0-9]+$/.test(ext)) return '';   // `2.0`, `260210` 같은 버전·날짜 꼬리
	return ext;
}

/** MIME → 확장자. 모르면 ''. `image/*` 처럼 서브타입이 유효하면 그걸 쓴다. */
export function extFromMime(mime: string | null | undefined): string {
	const m = String(mime || '').split(';')[0].trim().toLowerCase();
	if (!m) return '';
	if (MIME_EXT[m] !== undefined) return MIME_EXT[m];
	const sub = m.split('/')[1] || '';
	if (/^[a-z0-9]{1,8}$/.test(sub) && sub !== 'octet-stream' && sub !== '*') return sub;
	return '';
}

/**
 * URL 에서 파일명 유도. `?path=/file-upload/<id>/<이름.ext>` → 이름, 없으면 pathname 의 basename.
 * 라우트 경로(`/api/tpt/file` 등)나 쿼리만 있는 공유 링크는 파일명이 없으므로 '' 를 준다.
 * 전달된 파일 링크는 클라가 붙여 둔 `?name=` 을 우선 사용한다(서버는 이 값을 신뢰하지 않는다 — 경로는 토큰이 결정).
 */
export function fileNameFromUrl(url: string | null | undefined): string {
	const raw = String(url || '').trim();
	if (!raw || /^blob:/i.test(raw)) return '';
	let sp: URLSearchParams | null = null;
	let pathname = raw;
	try {
		const u = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://x');
		sp = u.searchParams;
		pathname = u.pathname + (u.hash || '');   // '#' 포함 파일명 보존 (2026-08-12 규약)
	} catch {
		const q = raw.indexOf('?');
		if (q >= 0) { try { sp = new URLSearchParams(raw.slice(q + 1)); } catch { /* keep */ } pathname = raw.slice(0, q); }
	}
	for (const key of ['name', 'filename', 'path']) {
		const v = sp?.get(key);
		if (!v) continue;
		const base = basenameOf(v);
		if (base && !ROUTE_BASENAMES.has(base.toLowerCase())) return base;
	}
	const base = basenameOf(pathname);
	if (!base || ROUTE_BASENAMES.has(base.toLowerCase())) return '';
	return base;
}

function basenameOf(p: string): string {
	const seg = String(p || '').split('/').filter(Boolean).pop() || '';
	try { return decodeURIComponent(seg); } catch { return seg; }
}

/**
 * 💾 저장용 파일명 확정 — 확장자가 반드시 붙은 이름을 만든다.
 *   ① 이름이 비었거나 일반 폴백('file'·'파일')이면 URL 쪽 이름으로 대체
 *   ② 확장자가 없으면 URL 쪽 확장자 → MIME 확장자 순으로 부착
 */
export function resolveDownloadName(name: string | null | undefined, url?: string | null, mime?: string | null): string {
	const urlName = fileNameFromUrl(url);
	let base = String(name || '').trim();
	if (!base || GENERIC_NAMES.has(base.toLowerCase())) base = urlName || base;
	if (!base) base = urlName || 'download';
	if (extOf(base)) return base;
	const ext = extOf(urlName) || extFromMime(mime);
	return ext ? `${base}.${ext}` : base;
}

/** 저장 경로/이름에 확장자가 없으면 붙인다(사용자가 다이얼로그에서 확장자를 지운 경우 보정). */
export function ensureExt(pathOrName: string, ext: string): string {
	const p = String(pathOrName || '');
	if (!ext || !p) return p;
	if (extOf(p) === ext.toLowerCase()) return p;
	if (extOf(p)) return p;            // 다른 확장자를 직접 지정했다면 사용자 의도 존중
	return `${p}.${ext}`;
}
